import { useState, useEffect, useRef } from 'react'
import { VITE_API_BASE } from '../config/apiConfig'

const API = VITE_API_BASE
// 复用现有 ADMIN_API_KEY 走 X-Admin-Key 内部 admin 通道（与 AIMarketCustomers.jsx 同模式）
const ADMIN_KEY = 'aim-admin-84deadf05b47284331cbf654'
const authHeaders = () => ({ 'X-Admin-Key': ADMIN_KEY })

const TEMPLATES = [
  { id: 'standard', name: '成长故事', desc: '适合生日会、毕业典礼等', icon: '📽️', duration: '3-5分钟' },
  { id: 'activity', name: '活动回顾', desc: '适合户外活动、节日活动', icon: '🎪', duration: '1-3分钟' },
  { id: 'learning', name: '学习集锦', desc: '适合课程展示、学习成果', icon: '📚', duration: '2-4分钟' },
  { id: 'annual', name: '年度总结', desc: '适合年终回顾、成长记录', icon: '🏆', duration: '5-8分钟' },
  { id: 'birthday', name: '生日祝福', desc: '适合生日会、节日庆祝', icon: '🎂', duration: '1-2分钟' },
]

const TRANSITIONS = [
  { id: 'fade', name: '淡入淡出' },
  { id: 'slide', name: '滑动切换' },
  { id: 'zoom', name: '缩放切换' },
  { id: 'none', name: '无过渡' },
]

export default function GrowthVideoProduction() {
  const [activeTab, setActiveTab] = useState('orders')
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [ffmpegOk, setFfmpegOk] = useState(null)

  const [step, setStep] = useState(1)
  const [children, setChildren] = useState([])
  const [selectedChild, setSelectedChild] = useState(null)
  const [childPhotos, setChildPhotos] = useState([])
  const [selectedPhotos, setSelectedPhotos] = useState([])
  const [loadingPhotos, setLoadingPhotos] = useState(false)
  const [creating, setCreating] = useState(false)
  const [claimCodes, setClaimCodes] = useState({})
  const [claimModal, setClaimModal] = useState(null)
  const [musicOptions, setMusicOptions] = useState([])
  const [photoSourceFilter, setPhotoSourceFilter] = useState('all') // all | activity | asset

  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    template: 'standard',
    transition: 'fade',
    music: '',
    photoDuration: 3,
    resolution: '1080p',
    subtitleEnabled: true,
    subtitleColor: 'white',
    subtitleFont: 'Microsoft YaHei',
    subtitleFontSize: 48,
    subtitlePosition: 'bottom',
    enableNarration: false,
    narrationText: '',
    voiceId: 'zh-CN-YunjianNeural',
    bgmVolume: 0.3,
  })

  // 从服务器加载音乐列表
  const loadMusicList = () => {
    fetch(`${API}/music/library`)
      .then(r => r.json())
      .then(d => {
        if (d.success && Array.isArray(d.data)) {
          const list = d.data.map(m => ({ id: m.name, name: m.name, file_path: m.file_path }))
          setMusicOptions(list)
          if (!list.find(m => m.id === form.music)) {
            setForm(f => ({ ...f, music: (list[0]?.id || '') }))
          }
        }
      })
      .catch(e => { console.error(e) })
  }

  useEffect(() => {
    loadOrders()
    checkFFmpeg()
    loadMusicList()
  }, [])

  const checkFFmpeg = () => {
    fetch(`${API}/health`)
      .then(r => r.json())
      .then(() => setFfmpegOk(true))
      .catch(() => setFfmpegOk(false))
  }

  const loadOrders = () => {
    setLoading(true)
    fetch(`${API}/growth-video/tasks`)
      .then(r => r.json())
      .then(d => {
        if (d.success && Array.isArray(d.data?.list)) {
          setOrders(d.data.list)
          const completed = d.data.list.filter(o => o.status === 'completed')
          completed.forEach(o => {
            fetch(`${API}/growth-video/claim-codes?child_id=${o.child_id}`)
              .then(r => r.json())
              .then(cd => {
                if (cd.success && cd.data?.length > 0) {
                  const mine = cd.data.find(c => c.task_id === o.id)
                  if (mine) setClaimCodes(prev => ({ ...prev, [o.id]: mine }))
                }
              })
              .catch(e => { console.error(e) })
          })
        } else {
          setOrders([])
        }
      })
      .catch(() => setOrders([]))
      .finally(() => setLoading(false))
  }

  const loadChildren = () => {
    fetch(`${API}/children`)
      .then(r => r.json())
      .then(d => {
        if (d.success && Array.isArray(d.data)) setChildren(d.data)
        else if (d.data?.children) setChildren(d.data.children)
      })
      .catch(e => { console.error(e) })
  }

  const loadChildPhotos = (childId) => {
    setLoadingPhotos(true)
    fetch(`${API}/children/${childId}/photos`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d.success) setChildPhotos(d.data.photos || [])
        else setChildPhotos([])
      })
      .catch(() => setChildPhotos([]))
      .finally(() => setLoadingPhotos(false))
  }

  const handleSelectChild = (child) => {
    setSelectedChild(child)
    loadChildPhotos(child.id)
    setForm(f => ({ ...f, title: `${child.name}的成长纪念视频` }))
    setStep(2)
  }

  const togglePhoto = (photo) => {
    setSelectedPhotos(prev => {
      const exists = prev.find(p => p.id === photo.id)
      if (exists) return prev.filter(p => p.id !== photo.id)
      if (prev.length >= 30) { alert('最多选择30张照片'); return prev }
      return [...prev, photo]
    })
  }

  const handleCreateTask = async () => {
    if (!selectedChild || selectedPhotos.length === 0) { alert('请选择孩子和照片'); return }
    setCreating(true)
    try {
      const res = await fetch(`${API}/growth-video/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          child_id: selectedChild.id,
          title: form.title || `${selectedChild.name}的成长纪念视频`,
          subtitle: form.subtitle,
          material_ids: selectedPhotos.map(p => p.id),
          photo_duration: form.photoDuration,
          transition: form.transition,
          music: form.music,
          template: form.template,
          resolution: form.resolution,
          subtitleEnabled: form.subtitleEnabled,
          subtitleColor: form.subtitleColor,
          subtitleFont: form.subtitleFont,
          subtitleFontSize: form.subtitleFontSize,
          subtitlePosition: form.subtitlePosition,
          enableNarration: form.enableNarration,
          narrationText: form.enableNarration ? form.narrationText : '',
          voiceId: form.voiceId,
          bgmVolume: form.bgmVolume,
        })
      })
      const d = await res.json()
      if (d.success) {
        alert(`任务已创建！任务ID: ${d.data.task_id}\n\n照片数量: ${selectedPhotos.length}张\n\n视频生成中，请稍候刷新查看状态。`)
        setStep(1); setSelectedChild(null); setSelectedPhotos([]); setChildPhotos([])
        loadOrders(); setActiveTab('orders')
      } else {
        alert(`创建失败: ${d.error}`)
      }
    } catch (e) { alert(`网络错误: ${e.message}`) }
    finally { setCreating(false) }
  }

  const getStatusBadge = (status) => {
    const map = {
      pending: { bg: '#fffbe6', color: '#faad14', text: '待处理' },
      processing: { bg: '#e6f4ff', color: '#1890ff', text: '制作中' },
      completed: { bg: '#f6ffed', color: '#52c41a', text: '已完成' },
      failed: { bg: '#fff1f0', color: '#ff4d4f', text: '生成失败' },
    }
    const s = map[status] || map.pending
    return <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 12, background: s.bg, color: s.color }}>{s.text}</span>
  }

  const formatDate = (d) => {
    if (!d) return '-'
    return new Date(d).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 20, marginBottom: 4 }}>🎬 成长视频制作</h2>
          <p style={{ color: '#666', fontSize: 13 }}>
            {ffmpegOk === false && <span style={{ color: '#ff4d4f' }}>⚠️ FFmpeg未就绪，请检查安装</span>}
            {ffmpegOk === true && <span style={{ color: '#52c41a' }}>✅ FFmpeg已就绪</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {['orders', 'create'].map(tab => (
            <button key={tab} onClick={() => { setActiveTab(tab); if (tab === 'create') loadChildren() }}
              style={{ padding: '8px 20px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, background: activeTab === tab ? '#1890ff' : '#f0f0f0', color: activeTab === tab ? '#fff' : '#666' }}>
              {tab === 'orders' ? '📋 订单列表' : '+ 新建任务'}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'orders' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { label: '总任务', value: orders.length, color: '#1890ff' },
            { label: '待处理', value: orders.filter(o => o.status === 'pending').length, color: '#faad14' },
            { label: '制作中', value: orders.filter(o => o.status === 'processing').length, color: '#1890ff' },
            { label: '已完成', value: orders.filter(o => o.status === 'completed').length, color: '#52c41a' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ color: '#999', fontSize: 12 }}>{s.label}</div>
              <div style={{ fontSize: 28, color: s.color, fontWeight: 600 }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'orders' && (
        <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>加载中...</div>
           : orders.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎬</div>
              <div style={{ color: '#666', marginBottom: 16 }}>暂无视频任务</div>
              <button onClick={() => { setActiveTab('create'); loadChildren() }} style={{ padding: '10px 24px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>创建第一个任务</button>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                  {['任务ID', '孩子', '标题', '照片', '状态', '领取码', '创建时间', '操作'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 13, color: '#666', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#999', fontFamily: 'monospace' }}>{o.id}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13 }}>{o.child_name || o.child_id || '-'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 500 }}>{o.title}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: '#666' }}>{o.material_count || 0}张</td>
                    <td style={{ padding: '12px 14px' }}>{getStatusBadge(o.status)}</td>
                    <td style={{ padding: '12px 14px' }}>{getStatusBadge(o.status)}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#999' }}>{formatDate(o.created_at)}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12 }}>
                      {o.status === 'completed' ? (
                        claimCodes[o.id] ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontFamily: 'monospace', color: '#1890ff', fontWeight: 600, fontSize: 13 }}>{claimCodes[o.id].claim_code}</span>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button onClick={() => navigator.clipboard.writeText(claimCodes[o.id].claim_code)} style={{ padding: '2px 8px', background: '#e6f4ff', color: '#1890ff', border: '1px solid #91d5ff', borderRadius: 3, cursor: 'pointer', fontSize: 11 }}>📋 复制</button>
                              <button onClick={() => setClaimModal({ code: claimCodes[o.id].claim_code, task: o })} style={{ padding: '2px 8px', background: '#fff1f0', color: '#ff4d4f', border: '1px solid #ffccc7', borderRadius: 3, cursor: 'pointer', fontSize: 11 }}>🔗 分享</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={async () => {
                            const res = await fetch(`${API}/growth-video/${o.id}/claim-code`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ parent_name: '', parent_phone: '' }) })
                            const d = await res.json()
                            if (d.success) { setClaimCodes(prev => ({ ...prev, [o.id]: { claim_code: d.data.claim_code, status: 'unused' } })); setClaimModal({ code: d.data.claim_code, task: o }) }
                            else alert(`错误: ${d.error}`)
                          }} style={{ padding: '5px 12px', background: '#faad14', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>🎁 生成领取码</button>
                        )
                      ) : o.status === 'failed' ? (
                        <span style={{ fontSize: 12, color: '#ff4d4f' }}>{o.error_message || '生成失败'}</span>
                      ) : (
                        <span style={{ fontSize: 12, color: '#999' }}>{o.status === 'processing' ? '⏳ 生成中...' : '⏳ 等待中'}</span>
                      )}
                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        {(o.status === 'completed' && (o.video_url || o.output_url)) && (
                          <a href={o.video_url || o.output_url} target="_blank" rel="noopener noreferrer" style={{ padding: '3px 8px', background: '#52c41a', color: '#fff', textDecoration: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 11, display: 'inline-block' }}>🎬 预览</a>
                        )}
                        <button onClick={async () => {
                          if (!confirm('确定删除任务 #' + o.id + ' ?')) return
                          try {
                            const res = await fetch(API + '/growth-video/tasks/' + o.id, { method: 'DELETE' })
                            const d = await res.json()
                            if (d.success) { loadOrders() }
                            else alert('删除失败: ' + (d.error || '未知错误'))
                          } catch(e) { alert('删除失败: ' + e.message) }
                        }} style={{ padding: '3px 8px', background: '#ff4d4f', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 11 }}>🗑️ 删除</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {orders.length > 0 && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={loadOrders} style={{ padding: '6px 16px', border: '1px solid #d9d9d9', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>🔄 刷新状态</button>
            </div>
          )}
        </div>
      )}

      {claimModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>🎁</div>
              <h3 style={{ fontSize: 18, marginBottom: 4 }}>领取码已生成</h3>
              <p style={{ fontSize: 13, color: '#999' }}>{claimModal.task?.title}</p>
            </div>
            <div style={{ background: '#f0f5ff', borderRadius: 12, padding: '16px 20px', textAlign: 'center', marginBottom: 20, border: '1px solid #adc6ff' }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>领取码</div>
              <div style={{ fontSize: 28, fontFamily: 'monospace', fontWeight: 700, color: '#1890ff', letterSpacing: 3 }}>{claimModal.code}</div>
            </div>
            <div style={{ background: '#fafafa', borderRadius: 8, padding: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>领取链接（复制发给家长）</div>
              <div style={{ fontSize: 11, color: '#1890ff', wordBreak: 'break-all', fontFamily: 'monospace' }}>{window.location.origin}/claim.html?code={claimModal.code}</div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => navigator.clipboard.writeText(claimModal.code)} style={{ flex: 1, padding: '10px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>📋 复制领取码</button>
              <button onClick={async () => { const url = `${window.location.origin}/claim.html?code=${claimModal.code}`; await navigator.clipboard.writeText(url) }} style={{ flex: 1, padding: '10px', background: '#52c41a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>🔗 复制领取链接</button>
            </div>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button onClick={() => setClaimModal(null)} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: 13 }}>关闭</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'create' && (
        <div style={{ background: '#fff', borderRadius: 8, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32 }}>
            {['选择孩子', '选择照片', '选择模板', '确认创建'].map((label, i) => {
              const s = i + 1
              const isActive = step === s
              const isDone = step > s
              return (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, background: isDone ? '#52c41a' : isActive ? '#1890ff' : '#f0f0f0', color: isDone || isActive ? '#fff' : '#999' }}>
                    {isDone ? '✓' : s}
                  </div>
                  <span style={{ fontSize: 14, color: isActive ? '#1890ff' : isDone ? '#52c41a' : '#999' }}>{label}</span>
                  {i < 3 && <span style={{ color: '#d9d9d9', margin: '0 4px' }}>›</span>}
                </div>
              )
            })}
          </div>

          {step === 1 && (
            <div style={{ marginBottom: 20 }}>
              <h3>选择孩子</h3>
              {children.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center' }}>
                  <button onClick={loadChildren} style={{ padding: '10px 24px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>加载孩子列表</button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                  {children.map(c => (
                    <div key={c.id} onClick={() => handleSelectChild(c)} style={{ padding: 16, border: `2px solid ${selectedChild?.id === c.id ? '#1890ff' : '#f0f0f0'}`, borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s' }}>
                      <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 8 }}>{c.avatar ? <img src={'/storage/children/' + c.avatar} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} /> : '👶'}</div>
                      <div style={{ textAlign: 'center', fontWeight: 500 }}>{c.name}</div>
                      <div style={{ textAlign: 'center', fontSize: 12, color: '#999', marginTop: 4 }}>{c.institution_name || c.school || ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3>选择照片 <span style={{ fontSize: 13, color: '#999', fontWeight: 'normal' }}>{selectedChild?.name} — 已选 {selectedPhotos.length} 张</span></h3>
                <div style={{ marginBottom: 12, display: 'flex', gap: 6 }}>
                  {[{ id: 'all', label: '全部' }, { id: 'activity', label: '活动照片' }, { id: 'asset', label: '家长素材' }].map(s => (
                    <button key={s.id} onClick={() => setPhotoSourceFilter(s.id)}
                      style={{ padding: '4px 12px', border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 12, background: photoSourceFilter === s.id ? '#1890ff' : '#f0f0f0', color: photoSourceFilter === s.id ? '#fff' : '#666' }}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setSelectedPhotos(childPhotos.slice(0, 20))} style={{ padding: '6px 14px', border: '1px solid #d9d9d9', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>全选前20张</button>
                  <button onClick={() => setSelectedPhotos([])} style={{ padding: '6px 14px', border: '1px solid #d9d9d9', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>清空</button>
                  <button onClick={() => setStep(3)} disabled={selectedPhotos.length === 0} style={{ padding: '6px 14px', background: selectedPhotos.length > 0 ? '#1890ff' : '#f0f0f0', color: selectedPhotos.length > 0 ? '#fff' : '#999', border: 'none', borderRadius: 4, cursor: selectedPhotos.length > 0 ? 'pointer' : 'not-allowed', fontSize: 13 }}>下一步 →</button>
                </div>
              </div>
              {loadingPhotos ? <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>加载照片中...</div>
               : childPhotos.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center' }}><div style={{ fontSize: 48, marginBottom: 12 }}>📷</div><div style={{ color: '#666', marginBottom: 8 }}>该孩子暂无照片</div><div style={{ fontSize: 13, color: '#999' }}>请先在活动管理中上传照片，或联系家长上传素材</div></div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                  {childPhotos.filter(p => photoSourceFilter === 'all' || p.source === photoSourceFilter).map(photo => {
                    const selected = selectedPhotos.find(p => p.id === photo.id)
                    return (
                      <div key={photo.id} onClick={() => togglePhoto(photo)} style={{ position: 'relative', cursor: 'pointer', borderRadius: 6, overflow: 'hidden', border: selected ? '3px solid #1890ff' : '3px solid transparent' }}>
                        <img src={photo.url} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f5f5f5" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23999" font-size="12">图片</text></svg>' }} />
                        {selected && <div style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', background: '#1890ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>✓</div>}
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 10, padding: '2px 4px', display: 'flex', justifyContent: 'space-between' }}>
                          <span>{photo.activity_title || photo.type}</span>
                          <span style={{ background: photo.source === 'asset' ? '#FF9800' : '#1890ff', borderRadius: 8, padding: '0 6px' }}>{photo.source === 'asset' ? '家长' : '活动'}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3>选择模板与设置</h3>
                <button onClick={() => setStep(4)} style={{ padding: '8px 20px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>下一步 →</button>
              </div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: '#333' }}>视频模板</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                  {TEMPLATES.map(t => (
                    <div key={t.id} onClick={() => setForm(f => ({ ...f, template: t.id }))} style={{ padding: 14, border: `2px solid ${form.template === t.id ? '#1890ff' : '#f0f0f0'}`, borderRadius: 8, cursor: 'pointer', background: form.template === t.id ? '#e6f4ff' : '#fff' }}>
                      <div style={{ fontSize: 24 }}>{t.icon}</div>
                      <div style={{ fontWeight: 500, fontSize: 14, marginTop: 6 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{t.desc}</div>
                      <div style={{ fontSize: 11, color: '#1890ff', marginTop: 4 }}>{t.duration}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: '#333' }}>过渡效果</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {TRANSITIONS.map(t => (
                      <button key={t.id} onClick={() => setForm(f => ({ ...f, transition: t.id }))} style={{ padding: '6px 14px', border: `1px solid ${form.transition === t.id ? '#1890ff' : '#d9d9d9'}`, background: form.transition === t.id ? '#e6f4ff' : '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 13, color: form.transition === t.id ? '#1890ff' : '#666' }}>{t.name}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: '#333' }}>背景音乐</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {musicOptions.length === 0 ? <div style={{ fontSize: 13, color: '#999' }}>加载音乐中...</div> : musicOptions.map(m => (
                      <button key={m.id} onClick={() => setForm(f => ({ ...f, music: m.id }))} style={{ padding: '6px 14px', border: `1px solid ${form.music === m.id ? '#1890ff' : '#d9d9d9'}`, background: form.music === m.id ? '#e6f4ff' : '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 13, color: form.music === m.id ? '#1890ff' : '#666' }}>{m.name}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#333' }}>每张照片时长: {form.photoDuration}秒</div>
                  <input type="range" min="2" max="6" step="0.5" value={form.photoDuration} onChange={e => setForm(f => ({ ...f, photoDuration: parseFloat(e.target.value) }))} style={{ width: '100%' }} />
                  <div style={{ fontSize: 12, color: '#999' }}>预计视频时长: {(selectedPhotos.length * form.photoDuration / 60).toFixed(1)}分钟</div>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#333' }}>分辨率</div>
                  <select value={form.resolution} onChange={e => setForm(f => ({ ...f, resolution: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 13 }}>
                    <option value="720p">720p (1280×720)</option>
                    <option value="1080p">1080p (1920×1080)</option>
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#333' }}>字幕颜色</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[{ id: 'white', color: '#fff', label: '白色' }, { id: 'yellow', color: '#FFEB3B', label: '黄色' }, { id: 'gold', color: '#FFD700', label: '金色' }, { id: 'blue', color: '#2196F3', label: '蓝色' }].map(c => (
                      <button key={c.id} onClick={() => setForm(f => ({ ...f, subtitleColor: c.id }))} title={c.label} style={{ width: 32, height: 32, border: form.subtitleColor === c.id ? '2px solid #1890ff' : '1px solid #d9d9d9', borderRadius: 6, background: c.color, cursor: 'pointer', boxShadow: form.subtitleColor === c.id ? '0 0 0 2px #1890ff33' : 'none' }} />
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#333' }}>字幕大小</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input type="range" min="28" max="72" value={form.subtitleFontSize} onChange={e => setForm(f => ({ ...f, subtitleFontSize: parseInt(e.target.value) }))} style={{ flex: 1 }} />
                    <span style={{ fontSize: 13, color: '#666', minWidth: 36 }}>{form.subtitleFontSize}px</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h3 style={{ fontSize: 16, marginBottom: 20 }}>确认创建</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
                <div style={{ padding: 16, background: '#f9f9f9', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>孩子</div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>{selectedChild?.name}</div>
                </div>
                <div style={{ padding: 16, background: '#f9f9f9', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>已选照片</div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>{selectedPhotos.length} 张</div>
                </div>
                <div style={{ padding: 16, background: '#f9f9f9', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>模板</div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>{(TEMPLATES.find(t => t.id === form.template) || {}).name}</div>
                </div>
                <div style={{ padding: 16, background: '#f9f9f9', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>预计时长</div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>{(selectedPhotos.length * form.photoDuration / 60).toFixed(1)} 分钟</div>
                </div>
                <div style={{ padding: 16, background: '#f9f9f9', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>字幕样式</div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>{form.subtitleColor === 'white' ? '白色' : form.subtitleColor === 'yellow' ? '黄色' : '金色'} / {form.subtitleFontSize}px</div>
                </div>
                <div style={{ padding: 16, background: form.enableNarration && form.narrationText ? '#f6f0ff' : '#f9f9f9', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>配音旁白</div>
                  <div style={{ fontSize: 16, fontWeight: 500, color: form.enableNarration && form.narrationText ? '#722ed1' : '#ccc' }}>
                    {form.enableNarration && form.narrationText ? '已开启' : '未开启配音'}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#333' }}>视频标题</div>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }} placeholder="请输入视频标题" />
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#333' }}>副标题（可选）</div>
                <input value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }} placeholder="如：记录美好童年时光" />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => setStep(3)} style={{ flex: 1, padding: '12px', border: '1px solid #d9d9d9', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>← 返回修改</button>
                <button onClick={handleCreateTask} disabled={creating} style={{ flex: 2, padding: '12px', background: creating ? '#ccc' : '#52c41a', color: '#fff', border: 'none', borderRadius: 6, cursor: creating ? 'not-allowed' : 'pointer', fontSize: 15, fontWeight: 500 }}>
                  {creating ? '创建中...' : '开始生成视频'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}